/**
 * Hook for fetching currently on-duty members.
 * Used by the Who's On Duty page.
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export interface OnShiftMember {
  display_name: string;
  position: string | null;
  apparatus: string | null;
  category: string;
  pwc_username: string | null;
  source: 'clocked_in' | 'planned';
}

interface OnShiftResponse {
  members: OnShiftMember[];
  count: number;
  server_time: string;
}

export interface UseOnShiftReturn {
  members: OnShiftMember[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useOnShift(): UseOnShiftReturn {
  const [members, setMembers] = useState<OnShiftMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function fetchOnShift() {
      try {
        const data = await apiFetch<OnShiftResponse>('/api/shift/on-duty');
        if (!cancelled) {
          setMembers(data.members);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load on-duty data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchOnShift();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return { members, loading, error, refresh };
}
